import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuthedFetch, parseJsonOrThrow } from './client';
import { RollupsResponse } from '../domain/api';

export type RollupEntityKind = 'floor' | 'group' | 'weather';

// How often to re-poll daily rollups. They are written once a night by the
// compute-rollups timer, so anything faster spends queries on data that
// cannot have moved. The Trends page holds nine of these keys at once and its
// 1y range asks each for 365 days, so a tight interval is nine large scans
// every cycle — and this project is cost-sensitive on Log Analytics ingestion.
const ROLLUP_POLL_MS = 30 * 60_000;

// Daily rollups for one floor or group, newest day first.
export function useRollupsQuery(kind: RollupEntityKind, name: string, days = 30) {
  const fetcher = useAuthedFetch();
  return useQuery({
    queryKey: ['rollups', kind, name, days],
    queryFn: async () => {
      const res = await fetcher(`/rollups/${kind}/${name}?days=${days}`);
      const parsed = await parseJsonOrThrow(
        res,
        `GET /rollups/${kind}/${name}`,
        (raw) => RollupsResponse.parse(raw),
      );
      return parsed.rollups;
    },
    // `days` is part of the query key, so switching the page range is a cache
    // MISS rather than a refetch: without this, `data` goes `undefined` and
    // `isLoading` `true` while nine fetches run, blanking every card on the
    // page to a skeleton at once. `history.ts` does the same thing, but there
    // the trigger is every poll; here it is a user clicking the range, where
    // the flash is even more obviously the wrong response to a deliberate act.
    // Unconditional `keepPreviousData` is safe here in a way it is not for
    // history: this hook is called once per entity with a fixed kind/name, so
    // the only part of the key that can change is `days` — the previous data
    // is always the same entity, just a different window.
    placeholderData: keepPreviousData,
    refetchInterval: ROLLUP_POLL_MS,
    // Match staleTime to the poll so a remount inside the same window reuses
    // the cached rows instead of firing another scan.
    staleTime: ROLLUP_POLL_MS,
  });
}
